// O Espelho oferece a integração — dentro da conversa, no momento em que ela
// faz sentido.
//
// Porta (a) da entrega de 29/07. A porta (b) é a entrada "Integrações" no
// hambúrguer do Espelho (src/config/navItems.ts). Uma sem a outra não
// resolve: o menu atende quem foi procurar, a proposta atende quem nem sabe
// que existe — e o número que motivou tudo isso diz que a segunda é a maioria
// (7 conexões ativas de Google Calendar, ZERO eventos vindos de integração em
// 7 dias, 108 vindos do Espelho).
//
// DECISÕES DE DESENHO, e por quê:
//
// 1. NÃO é uma tool do mirror-chat. Seriam três problemas de uma vez: o
//    system prompt do Espelho tem a REGRA #0 ("não puxe módulo que o usuário
//    não mencionou") que existe justamente para ele não ser intrometido, e
//    abrir exceção nela é caro; o `staticSystemBlock` precisa ficar
//    byte-idêntico entre Free e Premium por causa do cache de prompt, então
//    estado por usuário teria que ir no bloco dinâmico; e o mirror-chat hoje
//    lê 41 tabelas e `user_connections` não é uma delas — ele literalmente
//    não sabe o que está conectado. O chip resolve os três: decide no client,
//    com os dados que o client já tem.
//
// 2. Reusa `user_nudges` em vez de criar tabela. Ela já existe, já tem
//    dismissed_at / acted_on_at / cooldown_until, já tem RLS, e já está viva
//    (4.588 linhas, 4 tipos rodando hoje). `nudge_type` é text sem CHECK, então
//    tipos novos não precisam de migration nenhuma.
//
// 3. A sugestão sai do USO REAL dos módulos, nunca de uma lista fixa — a
//    decisão em si mora em src/lib/integrationSuggest.ts, que é puro e
//    testado.

import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import {
  decideSuggestion,
  type Suggestion,
  type Platform,
  type ModuleUsage,
} from "@/lib/integrationSuggest";

/** Dias que a sugestão dorme depois de dispensada uma vez. */
const COOLDOWN_DAYS = 21;
/** Dispensas consecutivas que desligam a sugestão daquele provider de vez. */
const STRIKE_THRESHOLD = 2;
/** Por quantos dias o strike desliga. */
const STRIKE_DISABLE_DAYS = 180;

const MS_PER_DAY = 86_400_000;
const USAGE_WINDOW_DAYS = 30;

interface NudgeRow {
  id: string;
  nudge_type: string;
  shown_at: string;
  dismissed_at: string | null;
  acted_on_at: string | null;
  cooldown_until: string | null;
}

export interface UseIntegrationSuggestionResult {
  suggestion: Suggestion | null;
  /** "Quero conectar" — leva para /settings/connections. */
  accept: () => void;
  /** "Agora não" — some, e não volta antes do cooldown. */
  dismiss: () => void;
}

function platformOf(): Platform {
  const p = Capacitor.getPlatform();
  if (p === "ios") return "ios";
  if (p === "android") return "android";
  return "web";
}

export function useIntegrationSuggestion(enabled: boolean): UseIntegrationSuggestionResult {
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [hidden, setHidden] = useState(false);
  const rowIdRef = useRef<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled || ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;

    const evaluate = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const userId = user.id;
        const sinceIso = new Date(Date.now() - USAGE_WINDOW_DAYS * MS_PER_DAY).toISOString();
        const sinceDate = sinceIso.slice(0, 10);

        // ── Histórico de nudges, conexões e uso, em paralelo ──────────────
        const [nudgeRes, connRes] = await Promise.all([
          (supabase as any)
            .from("user_nudges")
            .select("id, nudge_type, shown_at, dismissed_at, acted_on_at, cooldown_until")
            .eq("user_id", userId)
            .like("nudge_type", "integration_suggest_%")
            .order("shown_at", { ascending: false }),
          supabase
            .from("user_connections")
            .select("provider, status")
            .eq("user_id", userId),
        ]);

        if (nudgeRes.error) {
          // Fail-closed: sem saber o que já foi dispensado, propor de novo é
          // insistir com quem já disse não. Melhor não propor.
          console.error("[useIntegrationSuggestion] nudge history failed:", nudgeRes.error);
          return;
        }
        if (connRes.error) {
          // Idem: sem saber o que está conectado, a proposta pode oferecer o
          // que a pessoa já tem — o pior erro possível aqui.
          console.error("[useIntegrationSuggestion] connections failed:", connRes.error);
          return;
        }

        const history = (nudgeRes.data as NudgeRow[]) || [];
        const now = Date.now();

        // Um tipo está bloqueado se está em cooldown OU levou 2 dispensas
        // seguidas (sem nenhum aceite entre elas) nos últimos 180 dias.
        const blocked: string[] = [];
        const byType = new Map<string, NudgeRow[]>();
        for (const r of history) {
          const list = byType.get(r.nudge_type);
          if (list) list.push(r);
          else byType.set(r.nudge_type, [r]);
        }
        for (const [type, rows] of byType) {
          const latest = rows[0];
          if (latest?.cooldown_until && new Date(latest.cooldown_until).getTime() > now) {
            blocked.push(type);
            continue;
          }
          let streak = 0;
          let latestDismissed = 0;
          for (const r of rows) {
            if (r.acted_on_at) break; // aceitou em algum momento → zera
            if (r.dismissed_at) {
              streak++;
              const ts = new Date(r.dismissed_at).getTime();
              if (ts > latestDismissed) latestDismissed = ts;
            }
          }
          if (streak >= STRIKE_THRESHOLD && now - latestDismissed <= STRIKE_DISABLE_DAYS * MS_PER_DAY) {
            blocked.push(type);
          }
        }

        // Conexões: 'revoked' não conta como conectado (a pessoa desligou e
        // pode querer religar); qualquer outro status conta, inclusive
        // 'pending' — quem começou o fluxo não precisa ser convidado de novo.
        const connected = ((connRes.data as { provider: string; status: string }[]) || [])
          .filter((c) => c.status !== "revoked")
          .map((c) => c.provider);

        // ── Uso real dos módulos, 30 dias ─────────────────────────────────
        // Só dado que o USUÁRIO produziu. `source` importado fica de fora, ou
        // a integração passaria a justificar a si mesma: quem conectou o
        // Google Calendar teria "muitos eventos" e receberia a sugestão de
        // conectar o Google Calendar.
        const USER_SOURCES = ["manual", "voice", "mirror", "note", "plan"];
        const headCount = async (
          label: string,
          build: () => any,
        ): Promise<number> => {
          try {
            const { count, error } = await build();
            if (error) {
              // `warn`: o fallback é honesto (conta 0 → não sugere aquele
              // módulo). Nada que o usuário veio fazer se perde.
              console.warn("[useIntegrationSuggestion] count failed", label, error);
              return 0;
            }
            return count ?? 0;
          } catch (err) {
            console.warn("[useIntegrationSuggestion] count threw", label, err);
            return 0;
          }
        };

        const [workouts, sleepLogs, agendaEvents, tasks, notes, focusSessions] = await Promise.all([
          headCount("workouts", () => supabase
            .from("workout_sessions").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("started_at", sinceIso).in("source", USER_SOURCES)),
          headCount("sleep", () => supabase
            .from("sleep_logs").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("sleep_date", sinceDate).in("source", USER_SOURCES)),
          headCount("agenda", () => supabase
            .from("agenda_events").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("start_at", sinceIso).in("source", USER_SOURCES)),
          headCount("tasks", () => supabase
            .from("tasks").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("created_at", sinceIso).in("source", USER_SOURCES)),
          headCount("notes", () => supabase
            .from("notes").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("created_at", sinceIso).in("source", USER_SOURCES)),
          headCount("focus", () => supabase
            .from("focus_sessions").select("id", { count: "exact", head: true })
            .eq("user_id", userId).gte("created_at", sinceIso)),
        ]);

        const usage: ModuleUsage = { workouts, sleepLogs, agendaEvents, tasks, notes, focusSessions };

        const chosen = decideSuggestion({
          usage,
          connectedProviders: connected,
          platform: platformOf(),
          blockedNudgeTypes: blocked,
        });
        if (!chosen || cancelled) return;

        // Registra a exibição — é a linha que dismiss()/accept() vão atualizar.
        try {
          const { data: inserted, error: insErr } = await (supabase as any)
            .from("user_nudges")
            .insert({
              user_id: userId,
              nudge_type: chosen.nudgeType,
              shown_at: new Date().toISOString(),
              metadata: {
                provider: chosen.provider,
                signal_module: chosen.signalModule,
                signal_count: chosen.signalCount,
              },
            })
            .select("id")
            .single();
          if (insErr) {
            console.error("[useIntegrationSuggestion] insert shown failed:", insErr);
          } else if (inserted?.id) {
            rowIdRef.current = inserted.id as string;
          }
        } catch (err) {
          console.error("[useIntegrationSuggestion] insert threw:", err);
        }

        if (!cancelled) setSuggestion(chosen);
      } catch (err) {
        console.error("[useIntegrationSuggestion] evaluate threw:", err);
      }
    };

    void evaluate();
    return () => { cancelled = true; };
  }, [enabled]);

  const stamp = useCallback((field: "dismissed_at" | "acted_on_at") => {
    const rowId = rowIdRef.current;
    if (!rowId) return;
    const cooldown = new Date(Date.now() + COOLDOWN_DAYS * MS_PER_DAY).toISOString();
    const patch: Record<string, string> = { cooldown_until: cooldown };
    patch[field] = new Date().toISOString();
    void (supabase as any)
      .from("user_nudges")
      .update(patch)
      .eq("id", rowId)
      .then(({ error }: { error: unknown }) => {
        if (error) console.error("[useIntegrationSuggestion] " + field + " update failed:", error);
      });
  }, []);

  const accept = useCallback(() => {
    setHidden(true);
    stamp("acted_on_at");
  }, [stamp]);

  const dismiss = useCallback(() => {
    setHidden(true);
    stamp("dismissed_at");
  }, [stamp]);

  return { suggestion: hidden ? null : suggestion, accept, dismiss };
}
